import type { ReactNode } from "react";
import { IconDocument, IconWhatsApp } from "./icons";
import { EmptyState } from "./EmptyState";
import { IconBarChart } from "./icons";

interface Props {
  title: string;
  subtitle?: string;
  height?: number;
  children?: ReactNode;
  isEmpty?: boolean;
  emptyMessage?: string;
  loading?: boolean;
  actions?: ReactNode;
  onExport?: () => void;
  onWhatsApp?: () => void;
  className?: string;
}

export function ChartCard({
  title, subtitle, height = 260, children, isEmpty = false,
  emptyMessage, loading = false, actions, onExport, onWhatsApp, className = "",
}: Props) {
  return (
    <div className={`card overflow-hidden h-full flex flex-col ${className}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-1">
          {actions}
          {onWhatsApp && (
            <button onClick={onWhatsApp} className="btn-ghost p-2 text-gray-400 hover:text-tm-600" title="Enviar no WhatsApp">
              <IconWhatsApp className="w-4 h-4" />
            </button>
          )}
          {onExport && (
            <button onClick={onExport} className="btn-ghost p-2 text-gray-400" title="Exportar">
              <IconDocument className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Body — flex-1 + min-height (não height fixo): quando o grid estica o
          card pra bater com a altura do vizinho (ex. uma lista mais alta ao
          lado), o gráfico cresce junto em vez de deixar espaço vazio embaixo. */}
      <div className="p-5 flex-1 flex flex-col min-h-0">
        {loading ? (
          <div style={{ minHeight: height }} className="flex-1 bg-gray-50 rounded-lg animate-pulse" />
        ) : isEmpty ? (
          <div style={{ minHeight: height }} className="flex-1 flex items-center justify-center">
            <EmptyState
              icon={<IconBarChart className="w-6 h-6" />}
              title="Sem dados para exibir"
              description={emptyMessage ?? "Não há dados disponíveis para o período selecionado."}
              compact
            />
          </div>
        ) : (
          // relative + absolute inset-0 (em vez de só min-height no div do
          // gráfico): o Recharts `ResponsiveContainer` mede a própria altura
          // via height:100% do pai: isso falhava (media 0px) quando essa
          // altura do pai vinha só de flex-grow (sem um bloco posicionado),
          // porque o navegador podia resolver o layout antes do flex-grow
          // "assentar" — um filho absolute com inset:0 sempre tem altura
          // definida a partir da caixa do pai, sem essa ambiguidade.
          <div style={{ minHeight: height }} className="flex-1 relative">
            <div className="absolute inset-0">{children}</div>
          </div>
        )}
      </div>
    </div>
  );
}
